/**
 * IXD 값 편집기 - DSD 워크북 비교 데이터.
 *
 * 값 편집기 본문에 워크북 열을 붙이기 위한 서버 상태·스크롤 제어를 모은다.
 * (예전 `/ixd-workbook-match/preview` 화면이 들고 있던 로직을 그대로 옮겼다)
 *
 * 워크북을 고르지 않은 동안에는 모든 쿼리가 꺼져 있고 `isActive: false`다 —
 * 그때 값 편집기는 지금까지처럼 preview chunk 를 그대로 그린다.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  buildIxdMatchViewModel,
  buildPlanIndex,
  type PlanAvailability,
  type ReconstructedDsdTable,
} from "@/components/ixd-workbook-match";
import queries from "@/queries";
import type { FsType } from "@/apis/ixd-editor/types";
import { parseChunkNoteNumber } from "@/utils/ixd-editor/chunk-note-number";
import { notifyIxdMatchPreviewOpener } from "@/utils/ixd-workbook-match/tab-messages";

interface UseWorkbookCompareInput {
  reportId: string;
  /** 현재 재무제표 유형에 고른 워크북. 없으면 비교를 끈다 */
  workbookId: string | null;
  fsType: FsType;
  /** 선택 화면이 연 탭이면 준비 상태를 opener 에 알린다 */
  notifyOpener: boolean;
}

export const useWorkbookCompare = ({
  reportId,
  workbookId,
  fsType,
  notifyOpener,
}: UseWorkbookCompareInput) => {
  const queryClient = useQueryClient();
  const isActive = workbookId != null;
  // 쿼리 키는 항상 만들어두고 enabled 로만 끈다 — 훅 순서를 조건부로 바꾸지 않는다.
  const workbookKey = workbookId ?? "";

  const {
    data: workbook,
    error: workbookError,
    refetch: refetchWorkbook,
  } = useQuery({
    ...queries.workbook(workbookKey),
    enabled: isActive,
    retry: 3,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const matchPreviewQuery = queries
    .workbook(workbookKey)
    ._ctx.ixdMatchPreview(reportId);
  const {
    data: matchPreview,
    error: matchPreviewError,
    refetch: refetchMatchPreview,
  } = useQuery({
    ...matchPreviewQuery,
    enabled: isActive,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    structuralSharing: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // 값 동기화 plan — 표 구조가 달라 좌우 대조가 실패한 표의 폴백 소스.
  //
  // 화면의 필수 데이터가 아니므로 실패해도 막지 않는다. plan이 없으면 대조에
  // 성공한 표만 반영 가능한 기존 동작 그대로다(fail-closed).
  const valueSyncPlanQuery = queries
    .workbook(workbookKey)
    ._ctx.ixdValueSyncPlan(reportId);
  const { data: valueSyncPlan, isError: hasValueSyncPlanError } = useQuery({
    ...valueSyncPlanQuery,
    enabled: isActive,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  // 자식이 memo로 비교하므로 참조가 안정적이어야 한다.
  const plan = useMemo<PlanAvailability>(() => {
    if (valueSyncPlan) {
      return { status: "ready", index: buildPlanIndex(valueSyncPlan) };
    }
    return hasValueSyncPlanError ? { status: "error" } : { status: "loading" };
  }, [hasValueSyncPlanError, valueSyncPlan]);

  const isConsolidated = fsType === "consolidated";
  const selectedView = useMemo(
    () =>
      buildIxdMatchViewModel(
        workbook ? matchPreview : undefined,
        isConsolidated,
        true,
      ),
    [isConsolidated, matchPreview, workbook],
  );

  /**
   * 저장 성공 후 비교 데이터 재조회.
   *
   * staleTime: Infinity인 화면 캐시를 무효화에만 맡기지 않고 queryFn을 직접
   * 실행해 저장 직후 비교 API 재호출과 캐시 교체를 보장한다.
   */
  // 쿼리 객체는 매 렌더 새 참조라 ref 로 최신값만 넘긴다 — 콜백 자체는 고정되어
  // 저장 세션(useIxdEditSession)이 매 렌더 재구성되지 않는다.
  const saveRefetchRef = useRef({
    isActive,
    matchPreviewQuery,
    valueSyncPlanQuery,
  });
  saveRefetchRef.current = { isActive, matchPreviewQuery, valueSyncPlanQuery };
  const refetchAfterSave = useCallback(async () => {
    const current = saveRefetchRef.current;
    if (!current.isActive) return;
    await queryClient.invalidateQueries({
      queryKey: current.matchPreviewQuery.queryKey,
      exact: true,
      refetchType: "none",
    });
    await queryClient.fetchQuery({
      ...current.matchPreviewQuery,
      staleTime: 0,
      structuralSharing: false,
    });
    // plan은 저장된 상태에서 도출되므로 저장 직후 낡는다. 폴백 전용이라
    // 여기서 기다리지 않고 무효화만 걸어 다음 사용 시 새로 받게 한다.
    queryClient.invalidateQueries({
      queryKey: current.valueSyncPlanQuery.queryKey,
      exact: true,
    });
  }, [queryClient]);

  const chunks = selectedView.chunks;

  // IXD 대목차 제목에서 note number를 추출해 해당 대목차의 모든 DSD 표를 가져온다.
  const noteNumberByChunkId = useMemo(() => {
    const result = new Map<string, string>();
    for (const chunk of chunks) {
      // 재구성 DSD 표는 IXD 행에만 붙으므로, 워크북 표만 있는 목차의 노트 번호는 조회하지 않는다.
      if (chunk.dlViews.length === 0) continue;
      const n = parseChunkNoteNumber(chunk.title);
      if (n) result.set(chunk.id, n);
    }
    return result;
  }, [chunks]);
  const noteNumbers = useMemo(
    () => [...new Set(noteNumberByChunkId.values())],
    [noteNumberByChunkId],
  );
  const [shouldLoadDsdViews, setShouldLoadDsdViews] = useState(false);
  const {
    data: dsdViews,
    isFetching: isDsdViewsLoading,
    isError: hasDsdViewsError,
  } = useQuery({
    ...queries.workbook(workbookKey)._ctx.dsdViewsByNoteNumbers(noteNumbers),
    enabled: isActive && shouldLoadDsdViews && noteNumbers.length > 0,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });
  const dsdTablesByNoteNumber = useMemo(() => {
    const result = new Map<string, ReconstructedDsdTable[]>();
    for (const view of dsdViews?.views ?? []) {
      const tables = result.get(view.noteNumber) ?? [];
      tables.push({
        tableId: view.tableId,
        tableName: view.tableName,
        html: view.html,
      });
      result.set(view.noteNumber, tables);
    }
    return result;
  }, [dsdViews]);
  const getReconstructedDsdTables = useCallback(
    (chunkId: string): ReconstructedDsdTable[] => {
      const noteNumber = noteNumberByChunkId.get(chunkId);
      return noteNumber != null
        ? (dsdTablesByNoteNumber.get(noteNumber) ?? [])
        : [];
    },
    [dsdTablesByNoteNumber, noteNumberByChunkId],
  );
  const handleRequestDsdViews = useCallback(
    () => setShouldLoadDsdViews(true),
    [],
  );

  const isReady = !!workbook && !!matchPreview;
  const loadError = isReady
    ? null
    : (workbookError ?? matchPreviewError ?? null);

  // 선택 화면이 연 탭이면 준비/실패를 알려 로딩 표시를 닫게 한다.
  useEffect(() => {
    if (!isActive || !notifyOpener) return;
    if (isReady) {
      notifyIxdMatchPreviewOpener("ready");
      return;
    }
    if (loadError) notifyIxdMatchPreviewOpener("failed");
  }, [isActive, notifyOpener, isReady, loadError]);

  const retry = useCallback(() => {
    void refetchWorkbook();
    void refetchMatchPreview();
  }, [refetchMatchPreview, refetchWorkbook]);

  return {
    isActive,
    isReady,
    loadError,
    retry,
    /** 서버가 알려주는 워크북 이름 — SubHeader 의 DSD 워크북 슬롯에 쓴다 */
    workbookName: matchPreview?.workbookName,
    /** 워크북 보고 기간 — 비교 본문의 워크북 칸 기간 라벨(당반기 등)에 쓴다 */
    reportQuarter: workbook?.reportQuarter ?? null,
    chunks,
    matches: selectedView.matches,
    workbookOnly: selectedView.workbookOnly,
    workbookOnlyByChunkId: selectedView.workbookOnlyByChunkId,
    plan,
    getReconstructedDsdTables,
    isDsdViewsLoading,
    hasDsdViewsError,
    handleRequestDsdViews,
    refetchAfterSave,
  };
};
